import { useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

export default function SWAssessmentResultsPage() {
  const [assessments, setAssessments] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchAssessments = async () => {

    if (!user?.uid) {
      console.error('No logged-in social worker UID');
      setAssessments([]);
      setSelected(null);
      setLoading(false);
      return;
    }

    try {

      setLoading(true);

      const url =
        `https://guardiancloudcare.com/backend/api/get_child_assessments.php?uid=${encodeURIComponent(
          user.uid
        )}`;

      console.log('Fetching assessments for social worker:', user.uid);
      console.log('Assessment URL:', url);

      const res = await fetch(url);

      const data = await res.json();

      console.log('ASSESSMENT RESULTS RESPONSE:', data);

      if (!res.ok || data.success === false) {

        throw new Error(
          data?.error || `HTTP Error ${res.status}`
        );

      }

      const assessmentList = Array.isArray(data.data)
        ? data.data
        : [];

      setAssessments(assessmentList);

      setSelected((current: any) => {

        if (assessmentList.length === 0) {
          return null;
        }

        if (!current) {
          return assessmentList[0];
        }

        const existing = assessmentList.find(
          (item: any) =>
            item.case_number === current.case_number
        );

        return existing || assessmentList[0];

      });

    } catch (err) {

      console.error(
        'Assessment results fetch error:',
        err
      );

      setAssessments([]);
      setSelected(null);

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {

    if (user?.uid) {
      fetchAssessments();
    }

  }, [user?.uid]);

  const filtered = useMemo(() => {
    return assessments.filter((item) => {
      const keyword = search.toLowerCase();

      const fullName = `${item.first_name || ''} ${item.middle_name || ''} ${item.last_name || ''}`.toLowerCase();

      const matchSearch =
        item.case_number?.toLowerCase().includes(keyword) ||
        fullName.includes(keyword) ||
        item.risk_level?.toLowerCase().includes(keyword);

      const matchRisk =
        !riskFilter ||
        item.risk_level?.toLowerCase() === riskFilter.toLowerCase();

      return matchSearch && matchRisk;
    });
  }, [assessments, search, riskFilter]);

  const parseJson = (value: string, fallback: any) => {
    try {
      return JSON.parse(value || '');
    } catch {
      return fallback;
    }
  };

  const getFullName = (item: any) => {
    return `${item.first_name || ''} ${item.middle_name || ''} ${item.last_name || ''}`
      .replace(/\s+/g, ' ')
      .trim() || 'No child name';
  };

  const getRiskStyle = (risk: string) => {
    const r = (risk || '').toLowerCase();

    if (r.includes('critical')) return 'bg-red-100 text-red-700';
    if (r.includes('high')) return 'bg-orange-100 text-orange-700';
    if (r.includes('moderate')) return 'bg-yellow-100 text-yellow-700';
    return 'bg-green-100 text-green-700';
  };

  const total = assessments.length;
  const critical = assessments.filter((a) =>
    String(a.risk_level || '').toLowerCase().includes('critical')
  ).length;
  const high = assessments.filter((a) =>
    String(a.risk_level || '').toLowerCase().includes('high')
  ).length;
  const completed = assessments.filter((a) => a.status === 'completed').length;

  if (loading) {
    return (
      <AppLayout title="Assessment Results" breadcrumb={['Social Worker', 'Assessment Results']}>
        <div className="bg-white rounded-2xl p-10 text-center border">
          <i className="ri-loader-4-line animate-spin text-3xl text-teal-500" />
          <p className="text-sm text-gray-500 mt-3">Loading assessment results...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Assessment Results" breadcrumb={['Social Worker', 'Assessment Results']}>
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Assessment Results
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            View all saved child assessments and intervention plans.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon="ri-file-list-3-line" title="Total Assessments" value={total} color="blue" />
          <StatCard icon="ri-alarm-warning-line" title="Critical Risk" value={critical} color="red" />
          <StatCard icon="ri-alert-line" title="High Risk" value={high} color="orange" />
          <StatCard icon="ri-checkbox-circle-line" title="Completed" value={completed} color="green" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-5">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative md:col-span-2">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search case number, child name, or risk level..."
                  className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 outline-none"
                />
              </div>

              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                className="px-4 py-3 rounded-2xl border border-gray-200 outline-none"
              >
                <option value="">All Risk Levels</option>
                <option value="CRITICAL RISK">Critical Risk</option>
                <option value="HIGH RISK">High Risk</option>
                <option value="MODERATE RISK">Moderate Risk</option>
                <option value="LOW RISK">Low Risk</option>
              </select>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr className="text-left text-sm text-gray-500">
                    <th className="px-5 py-4">Case Number</th>
                    <th className="px-5 py-4">Child</th>
                    <th className="px-5 py-4">Incident Type</th>
                    <th className="px-5 py-4">Risk Level</th>
                    <th className="px-5 py-4">Score</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Date</th>
                    <th className="px-5 py-4">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-gray-400 py-10">
                        No assessment results found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((item) => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-5 py-4 text-sm font-semibold text-teal-700">
                          {item.case_number}
                        </td>

                        <td className="px-5 py-4">
                          <div>
                            <p className="font-semibold text-gray-800">
                              {getFullName(item)}
                            </p>
                            <p className="text-xs text-gray-400">
                              {item.age || '-'} yrs • {item.gender || '-'}
                            </p>
                          </div>
                        </td>

                        <td className="px-5 py-4 text-sm text-gray-600">
                          {item.incident_type || 'No incident'}
                        </td>

                        <td className="px-5 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRiskStyle(item.risk_level)}`}>
                            {item.risk_level}
                          </span>
                        </td>

                        <td className="px-5 py-4 font-bold text-gray-800">
                          {item.risk_score}%
                        </td>

                        <td className="px-5 py-4">
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                            {item.status}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-sm text-gray-500">
                          {item.created_at}
                        </td>

                        <td className="px-5 py-4">
                          <button
                            onClick={() => setSelected(item)}
                            className="w-10 h-10 rounded-xl border border-gray-200 hover:bg-gray-50"
                          >
                            <i className="ri-eye-line" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {selected && (
            <div className="space-y-5">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {getFullName(selected)}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {selected.case_number} • {selected.age || '-'} yrs • {selected.gender || '-'}
                    </p>
                  </div>

                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRiskStyle(selected.risk_level)}`}>
                    {selected.risk_level}
                  </span>
                </div>
              </div>

              <DetailCard
                title="AI Summary"
                icon="ri-brain-line"
                content={selected.ai_summary || 'No summary available.'}
              />

              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600" />
                  Recommendations
                </h3>

                <div className="space-y-2">
                  {parseJson(selected.recommendations, []).map((rec: string, index: number) => (
                    <div key={index} className="flex gap-3 text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600 mt-0.5" />
                      <span>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <i className="ri-road-map-line text-purple-600" />
                  Intervention Plan
                </h3>

                {parseJson(selected.intervention_plan, []).map((plan: any, index: number) => (
                  <div key={index} className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-400">Primary Goal</p>
                      <p className="font-semibold text-gray-800">
                        {plan.primary_goal || 'No primary goal'}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-400 mb-2">Objectives</p>
                      {(plan.objectives || []).map((obj: string, i: number) => (
                        <p key={i} className="text-sm text-gray-600">
                          • {obj}
                        </p>
                      ))}
                    </div>

                    <div>
                      <p className="text-xs text-gray-400 mb-2">Strategies</p>
                      {(plan.strategies || []).map((s: any, i: number) => (
                        <div key={i} className="bg-gray-50 rounded-xl p-3 mb-2">
                          <p className="font-semibold text-sm text-gray-800">
                            {s.strategy}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {s.description}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {s.responsible_person} • {s.timeline}
                          </p>
                        </div>
                      ))}
                    </div>

                    {plan.notes && (
                      <div>
                        <p className="text-xs text-gray-400">Notes</p>
                        <p className="text-sm text-gray-600">{plan.notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <DetailCard
                title="Assessment Notes"
                icon="ri-sticky-note-line"
                content={selected.notes || 'No notes provided.'}
              />
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function StatCard({ icon, title, value, color }: any) {
  const colors: any = {
    blue: 'bg-blue-100 text-blue-600',
    red: 'bg-red-100 text-red-600',
    orange: 'bg-orange-100 text-orange-600',
    green: 'bg-green-100 text-green-600',
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${colors[color]}`}>
        <i className={`${icon} text-xl`} />
      </div>
      <p className="text-sm text-gray-500 mt-4">{title}</p>
      <h2 className="text-3xl font-bold text-gray-900 mt-1">{value}</h2>
    </div>
  );
}

function DetailCard({ title, icon, content }: any) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
      <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
        <i className={`${icon} text-blue-600`} />
        {title}
      </h3>
      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
        {content}
      </p>
    </div>
  );
}